import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Link, Typography, Skeleton, Stack, useMediaQuery } from '@mui/material'; // Skeleton
import { useTheme } from '@mui/material/styles';
import { Label } from './index';

const LabelField = (props) => {
  const { data, isLoading, label, minWidth = '115px', noWrap = true, showIfEmpty, showLabel = true, sx } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (isLoading && !showLabel) {
    return (
      <Skeleton>
        <DataEl {...props} />
      </Skeleton>
    );
  }

  if ((!data && !showIfEmpty) || (!data && isMobile)) {
    return;
  }

  if (!showLabel) {
    return <DataEl {...props} />;
  }

  const content = (
    <Stack sx={{ ...styleWrapper(noWrap), ...sx }}>
      <Label label={label} minWidth={minWidth} />
      <DataEl {...props} />
    </Stack>
  );

  if (isLoading) {
    return <Skeleton>{content}</Skeleton>;
  }

  return content;
};

const styleWrapper = (noWrap) => ({
  flexDirection: 'row',
  flexWrap: noWrap ? 'nowrap' : 'wrap',
  alignItems: 'baseline'
});

LabelField.propTypes = {
  isLoading: PropTypes.bool,
  data: PropTypes.string,
  label: PropTypes.string,
  fontSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  minWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showIfEmpty: PropTypes.bool,
  showLabel: PropTypes.bool,
  sx: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  noWrap: PropTypes.bool,
  onClick: PropTypes.func
};

const DataEl = (props) => {
  const {
    component = 'span',
    data = '',
    fieldFontWeight = 400,
    fontSize,
    href = '',
    id,
    rel = 'noopener noreferrer',
    router,
    target = '',
    onClick
  } = props;
  const theme = useTheme();

  const styleRouterLinkWrapper = {
    a: {
      fontSize,
      fontWeight: fieldFontWeight,
      color: theme.palette.primary.main,
      textDecoration: 'none'
    },
    ':hover': {
      textDecoration: 'underline',
      color: theme.palette.primary.main
    }
  };

  let styleNoData = {};
  if (!data) {
    styleNoData = {
      fontStyle: 'italic',
      color: theme.palette.grey[500]
    };
  }

  const styleLink = {
    wordBreak: 'break-word',
    ':hover': {
      cursor: 'pointer'
    }
  };

  const styleText = {
    fontWeight: fieldFontWeight,
    cursor: 'default',
    wordBreak: 'break-word',
    overflowWrap: 'normal',
    ...styleNoData
  };

  if (router) {
    return (
      <Box sx={styleRouterLinkWrapper}>
        <RouterLink id={id} variant="body2" to={router} onClick={onClick}>
          {data}
        </RouterLink>
      </Box>
    );
  }

  if (data && href) {
    return (
      <Link id={id} variant="body2" href={href} target={target} rel={rel} sx={styleLink}>
        {data}
      </Link>
    );
  }

  return (
    <Typography id={id} component={component} variant="body2" sx={styleText}>
      {data?.length > 0 ? data : 'Non renseigné'}
    </Typography>
  );
};

DataEl.propTypes = {
  component: PropTypes.string,
  data: PropTypes.string,
  fieldFontWeight: PropTypes.number,
  fontSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  href: PropTypes.string,
  id: PropTypes.string,
  rel: PropTypes.string,
  router: PropTypes.string,
  target: PropTypes.string,
  onClick: PropTypes.func
};

export default LabelField;
